'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Grid, Stack, TextField, MenuItem, Button, Box, Paper, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import { datePickerStyle } from '../../fuel/constant';

import Achievement from './Achievement';
import VehicleStatus from './VehicleStatus';
import TopRitaseChart from './TopRitaseChart';
import WorstIdleUnitChart from './WorstIdleUnitChart';

import { useVehicleStatusDashboardDynamic } from '../../../../stores/vehicleStatusStore';
import { useMasterVehicleList } from '../../../../stores/masterVehicleStore';
import { fetchRetase, fetchRetaseDetail, dashboardCache } from '../../../../stores/retaseStore';
import { fetcherDataReportTrip } from '../../../../stores/reportStore';
import { useLanguage } from '../../../../utils/i18n';
import { timezone } from '../../../../utils/timeZone';
const selectFieldStyle = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#485D9A' },
    '&:hover fieldset': { borderColor: '#485D9A' },
    '&.Mui-focused fieldset': { borderColor: '#485D9A' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
};

const PeriodeRange = () => {
  const [autoRefresh, setAutoRefresh] = useState(0);
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState(dayjs().format('YYYY-MM-DDT00:00:00'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DDTHH:mm:59'));
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const { t } = useLanguage();
  const [achievementData, setAchievementData] = useState([]);

  const [topRitaseData, setTopRitaseData] = useState({ categories: [], series: [] });
  const [worstIdleData, setWorstIdleData] = useState({ categories: [], series: [] });

  const { data: vehicleList, isLoading: isMasterLoading } = useMasterVehicleList();
  const { data: vehicleStatusData, isLoading: isStatusLoading } = useVehicleStatusDashboardDynamic(100);

  const abortControllerRef = useRef(null);

  const fetchDashboardData = useCallback(async (startStr, endStr, signal, vehicleList) => {
    setLoading(true);
    setLoadingProgress(0);
    setLoadingText('Memeriksa data...');
    try {
      const startIso = dayjs(startStr).format('YYYY-MM-DDTHH:mm:ss');
      const endIso = dayjs(endStr).format('YYYY-MM-DDTHH:mm:ss');
      const companyId = localStorage.getItem('companyId') || '';

      const isToday = dayjs(startStr).isSame(dayjs(), 'day') && dayjs(endStr).isSame(dayjs(), 'day');
      const cacheKey = `periodeRange_${companyId}_${startIso}_${endIso}`;

      if (!isToday) {
        const cachedData = dashboardCache.get(cacheKey);
        if (cachedData) {
          setAchievementData(cachedData.achievementData);
          setTopRitaseData(cachedData.topRitaseData);
          setWorstIdleData(cachedData.worstIdleData);
          setLoading(false);
          return;
        }
      }

      // 1. Gunakan data kendaraan dari useMasterVehicleList agar sinkron dengan DashboardRevenue
      let allVehicles = Array.isArray(vehicleList) ? vehicleList : [];
      // Jika master kendaraan masih dimuat, effect dependency akan men-trigger ulang fungsi ini setelah vehicleList terisi.

      if (allVehicles.length === 0) {
        // Jika belum ada data (atau masih fetch master), kita biarkan return awal.
        // Komponen tabel di bawah tetap akan menampikan loading spinner berdasarkan prop `isMasterLoading` yang di-pass.
        setLoading(false);
        return;
      }

      // Objek penampung untuk kalkulasi ranking
      const ritaseCounts = {};
      const idleCounts = {};
      const allCombinedData = [];

      // 2. LOOP PER KENDARAAN (Dibuat chunking)
      const totalVehicles = allVehicles.length;
      let completedVehicles = 0;
      setLoadingText(`Preparing data for ${totalVehicles} vehicles...`);

      const chunkSize = 5;
      for (let i = 0; i < totalVehicles; i += chunkSize) {
        if (signal.aborted) throw new Error('CanceledError');
        const chunk = allVehicles.slice(i, i + chunkSize);

        await Promise.all(
          chunk.map(async (v) => {
            const vId = v.id ?? v.vehicle_id;
            const licensePlate = v.license_plate ?? v.name ?? String(vId);
            // 4. Hit API menggunakan company_id milik vehicle
            const vCompanyId = v.company_id ?? v.company ?? companyId;

            if (!ritaseCounts[licensePlate]) ritaseCounts[licensePlate] = 0;
            if (!idleCounts[licensePlate]) idleCounts[licensePlate] = 0;
            try {
              try {
                const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
                const idleRes = await fetcherDataReportTrip(vId, startStr, endStr, String(vCompanyId), tz);
                let reportDataList = [];
                if (Array.isArray(idleRes?.data)) reportDataList = idleRes.data;
                else if (Array.isArray(idleRes?.data?.data)) reportDataList = idleRes.data.data;
                else if (Array.isArray(idleRes)) reportDataList = idleRes;
                else if (idleRes && typeof idleRes === 'object' && Object.keys(idleRes).length > 0) reportDataList = [idleRes];

                let totalIdleSec = 0;
                reportDataList.forEach((item) => {
                  const idleStr = item.duration_engine_idle || item.engine_idle_duration || item.idle_duration || '0:0';
                  if (typeof idleStr === 'string' && idleStr.includes(':')) {
                    const parts = idleStr.split(':');
                    totalIdleSec += parseInt(parts[0], 10) * 3600 + parseInt(parts[1] || 0, 10) * 60 + parseInt(parts[2] || 0, 10);
                  } else if (!isNaN(Number(idleStr))) {
                    totalIdleSec += Number(idleStr);
                  }
                });
                idleCounts[licensePlate] += totalIdleSec;
              } catch (err) {
                if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
                  console.warn(`Failed fetch idle data report trip vID: ${vId}:`, err?.message || err);
                }
              }

              // === RITASE CALCULATION (TopRitaseChart) ===
              let vehicleTrips = [];
              const revenueCache = isToday
                ? dashboardCache.get(`revenue_${vCompanyId}_today`) || dashboardCache.get(`revenue_${companyId}_today`)
                : null;

              if (revenueCache) {
                // Reuse from DashboardRevenue if we're searching for "Today"
                const rawTrips = revenueCache.rawTrips || [];
                const vTrips = rawTrips.filter((t) => String(t.vehicle_id) === String(vId));

                vehicleTrips = vTrips.map((t) => ({
                  ...t,
                  license_plate: licensePlate
                }));
              } else {
                let dates = [];
                let curr = dayjs(startStr).startOf('day');
                const end = dayjs(endStr).startOf('day');
                while (curr.isBefore(end) || curr.isSame(end, 'day')) {
                  dates.push(curr);
                  curr = curr.add(1, 'day');
                }

                const dailyPromises = dates.map(async (dateObj) => {
                  let dStartIso = dateObj.startOf('day');
                  let dEndIso = dateObj.endOf('day');

                  if (dateObj.isSame(dayjs(startStr), 'day')) dStartIso = dayjs(startStr);
                  if (dateObj.isSame(dayjs(endStr), 'day')) dEndIso = dayjs(endStr);

                  const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
                  const finalStartIso = dStartIso.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                  const finalEndIso = dEndIso.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

                  try {
                    const queryParams = {
                      vehicleId: String(vId),
                      companyId: String(vCompanyId),
                      tz,
                      startTime: finalStartIso,
                      endTime: finalEndIso,
                      signal
                    };

                    const response = await fetchRetase(queryParams);
                    const apiDataRaw = response?.data.retase ?? [];

                    const uniqueTripsMap = new Map();
                    apiDataRaw.forEach((tripData) => {
                      const routes = tripData.routes || [];
                      const details = tripData.details || [];

                      const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                      const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;

                      let earliestStart = null;
                      let latestEnd = null;
                      details.forEach((d) => {
                        const st = dayjs(d.start);
                        const en = dayjs(d.end);
                        if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                        if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                      });

                      const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                      const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

                      const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                      if (!uniqueTripsMap.has(uniqueKey)) {
                        uniqueTripsMap.set(uniqueKey, tripData);
                      }
                    });
                    const apiData = Array.from(uniqueTripsMap.values());

                    const combinedTripsNested = await Promise.all(
                      apiData.map(async (tripData) => {
                        const routes = tripData.routes || [];
                        const details = tripData.details || [];

                        if (details.length === 0) {
                          return [
                            {
                              route_trip_id: tripData.route_trip_id,
                              license_plate: licensePlate,
                              vehicle_id: vId,
                              in_time: tripData.start_time || tripData.in_time || 'no-start',
                              out_time: tripData.end_time || tripData.out_time || 'no-end',
                              duration: 0,
                              visits: routes,
                              details: [],
                              ritase_count: tripData.total_trips || 1
                            }
                          ];
                        }

                        const detailPromises = details.map(async (d, dIdx) => {
                          const st = dayjs(d.start);
                          const en = dayjs(d.end);
                          const finalStartIso = st.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                          const finalEndIso = en.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

                          let nextRouteTimeIso = undefined;
                          if (details.length > 1 && dIdx < details.length - 1) {
                            const nextSt = dayjs(details[dIdx + 1].start);
                            nextRouteTimeIso = nextSt.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                          }

                          const finalStart = st.format('YYYY-MM-DD HH:mm:ss');
                          const finalEnd = en.format('YYYY-MM-DD HH:mm:ss');

                          let actualStartTime = finalStart;
                          let actualEndTime = finalEnd;

                          try {
                            const detailRes = await fetchRetaseDetail({
                              companyId: String(vCompanyId),
                              vehicleId: String(vId),
                              routeTripId: String(tripData.route_trip_id),
                              startTime: finalStartIso,
                              endTime: finalEndIso,
                              nextRouteTime: nextRouteTimeIso,
                              tz,
                              page: 0,
                              rowsPerPage: 10000,
                              signal
                            });
                            const deepDetailLogs = detailRes?.data?.data || detailRes?.data || [];
                            if (deepDetailLogs.length > 0) {
                              actualStartTime = dayjs(deepDetailLogs[0].start_time).format('YYYY-MM-DD HH:mm:ss');
                              actualEndTime = dayjs(deepDetailLogs[deepDetailLogs.length - 1].end_time).format('YYYY-MM-DD HH:mm:ss');
                            }
                          } catch (detailErr) {
                            if (detailErr.name !== 'CanceledError' && detailErr.code !== 'ERR_CANCELED') {
                              console.error(detailErr);
                            }
                          }

                          return {
                            route_trip_id: tripData.route_trip_id,
                            license_plate: licensePlate,
                            vehicle_id: vId,
                            in_time: actualStartTime,
                            out_time: actualEndTime,
                            duration: d.dur || 0,
                            visits: routes,
                            details: [d],
                            ritase_count: 1
                          };
                        });

                        return await Promise.all(detailPromises);
                      })
                    );

                    return combinedTripsNested.flat();
                  } catch {
                    return [];
                  }
                });

                const dailyResults = await Promise.all(dailyPromises);
                vehicleTrips = dailyResults.flat();
              } // end else

              let vehicleTotalRitase = vehicleTrips.reduce((acc, curr) => acc + (curr.ritase_count || curr.details?.length || 1), 0);
              allCombinedData.push(...vehicleTrips);

              ritaseCounts[licensePlate] += vehicleTotalRitase;
            } catch (err) {
              if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                console.error(`Gagal fetch data ritase vID: ${vId}`, err);
              }
            } finally {
              completedVehicles++;
              setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
              setLoadingText(`Processing unit: ${licensePlate} (${completedVehicles}/${totalVehicles})`);
            }
          })
        );
      }

      setLoadingText('Menyelesaikan kalkulasi data...');

      // === KALKULASI TOP 10 RITASE ===
      const sortedRitase = Object.entries(ritaseCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      const topRitase = {
        categories: sortedRitase.map((item) => item[0]),
        series: [{ name: 'Ritase', data: sortedRitase.map((item) => item[1]) }]
      };
      setTopRitaseData(topRitase);

      // === KALKULASI TOP 10 WORST IDLE (Konversi detik ke Jam) ===
      const sortedIdle = Object.entries(idleCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10);

      const worstIdle = {
        categories: sortedIdle.map((item) => item[0]),
        series: [{ name: 'Idle Hours', data: sortedIdle.map((item) => Number((item[1] / 3600).toFixed(2))) }]
      };
      setWorstIdleData(worstIdle);

      // Lempar seluruh data raw+visit ke komponen Achievement
      setAchievementData(allCombinedData);

      if (!isToday) {
        dashboardCache.set(cacheKey, { achievementData: allCombinedData, topRitaseData: topRitase, worstIdleData: worstIdle });
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Gagal mengambil data report', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fromDate && toDate) {
      abortControllerRef.current = new AbortController();
      fetchDashboardData(fromDate, toDate, abortControllerRef.current.signal, vehicleList);
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDashboardData, vehicleList]);

  const handleGenerateClick = () => {
    if (!fromDate || !toDate) return alert(t('revenue.pleaseCompleteDate', 'Please complete the date!'));
    abortControllerRef.current = new AbortController();
    fetchDashboardData(fromDate, toDate, abortControllerRef.current.signal, vehicleList);
  };

  // AUTO REFRESH
  useEffect(() => {
    if (autoRefresh <= 0 || periodType !== 'today') return;

    const intervalMs = autoRefresh * 60 * 1000;
    const timer = setInterval(() => {
      const nowStr = dayjs().format('YYYY-MM-DDTHH:mm:59');
      const startStr = dayjs().format('YYYY-MM-DDT00:00:00');

      setFromDate(startStr);
      setToDate(nowStr);

      abortControllerRef.current = new AbortController();
      fetchDashboardData(startStr, nowStr, abortControllerRef.current.signal, vehicleList);
    }, intervalMs);

    return () => {
      clearInterval(timer);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, periodType, fetchDashboardData]);

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setPeriodType(val);
    if (val !== 'today') {
      setAutoRefresh(0);
    }
    const nowStr = dayjs().format('YYYY-MM-DDTHH:mm:59');

    if (val === 'today') {
      setFromDate(dayjs().format('YYYY-MM-DDT00:00:00'));
      setToDate(nowStr);
    } else if (val === 'last_week') {
      setFromDate(dayjs().subtract(7, 'day').format('YYYY-MM-DDT00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DDT23:59:59'));
    } else if (val === 'last_month') {
      setFromDate(dayjs().subtract(1, 'month').format('YYYY-MM-DDT00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DDT23:59:59'));
    } else {
      // default custom
      setFromDate(dayjs().format('YYYY-MM-DDT00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DDT23:59:59'));
    }
  };

  // --- Helper conditional rendering widgets ---
  const appSettingStr = localStorage.getItem('appSetting');
  const appSetting = appSettingStr ? JSON.parse(appSettingStr) : {};
  const widgetsConfig = appSetting?.widgets || null;
  const showWidget = (key) => {
    if (!widgetsConfig) return true;
    if (!widgetsConfig.revenue) return true;
    return widgetsConfig.revenue[key] !== false;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', height: '100%' }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mt: 1, mb: 3 }}>
          <Grid item>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                size="medium"
                label={t('revenue.refreshEvery', 'Refresh Every')}
                value={autoRefresh}
                onChange={(e) => setAutoRefresh(Number(e.target.value))}
                disabled={periodType !== 'today'}
                sx={{ minWidth: 150, ...selectFieldStyle }}
              >
                <MenuItem value={0}>{t('revenue.off', 'Off')}</MenuItem>
                <MenuItem value={10}>{t('dashboardFuel.minutes10', '10 Minutes')}</MenuItem>
                <MenuItem value={30}>{t('dashboardFuel.minutes30', '30 Minutes')}</MenuItem>
                <MenuItem value={60}>{t('dashboardFuel.minutes60', '60 Minutes')}</MenuItem>
              </TextField>

              <Button
                variant="outlined"
                onClick={handleGenerateClick}
                disabled={loading}
                sx={{
                  width: 41,
                  height: 41,
                  minWidth: 41,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0,
                  borderColor: '#485D9A',
                  '&:hover': { borderColor: '#485D9A' }
                }}
              >
                <HistoryIcon sx={{ fontSize: 24, color: '#485D9A', fontWeight: 700 }} />
              </Button>
            </Stack>
          </Grid>

          <Grid item>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack spacing={1}>
                <TextField
                  select
                  size="medium"
                  label={t('revenue.period', 'Period')}
                  value={periodType}
                  onChange={handlePeriodChange}
                  sx={{ minWidth: 150, ...selectFieldStyle }}
                >
                  <MenuItem value="today">{t('dashboardFuel.today', 'Today')}</MenuItem>
                  <MenuItem value="last_week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="last_month">{t('time.lastMonth', 'Last month')}</MenuItem>
                  <MenuItem value="custom">{t('time.custom', 'Custom')}</MenuItem>
                </TextField>
              </Stack>

              {periodType === 'custom' && (
                <Grid container spacing={2}>
                  <Grid>
                    <DateTimePicker
                      label={t('revenue.fromDate', 'From Date')}
                      format="DD/MM/YYYY HH:mm"
                      ampm={false}
                      timeSteps={{ minutes: 1 }}
                      value={fromDate ? dayjs(fromDate) : null}
                      maxDate={toDate ? dayjs(toDate) : undefined}
                      onChange={(newValue) => setFromDate(newValue ? newValue.format('YYYY-MM-DDTHH:mm:ss') : '')}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: datePickerStyle
                        }
                      }}
                    />
                  </Grid>
                  <Grid>
                    <DateTimePicker
                      label={t('revenue.toDate', 'To Date')}
                      format="DD/MM/YYYY HH:mm"
                      ampm={false}
                      timeSteps={{ minutes: 1 }}
                      value={toDate ? dayjs(toDate) : null}
                      onChange={(newValue) => setToDate(newValue ? newValue.format('YYYY-MM-DDTHH:mm:ss') : '')}
                      minDate={fromDate ? dayjs(fromDate) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: datePickerStyle
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              <Button
                variant="contained"
                sx={{ bgcolor: '#485D9A', '&:hover': { bgcolor: '#3b4c80' } }}
                color="primary"
                onClick={handleGenerateClick}
                disabled={loading}
              >
                {loading ? t('revenue.searching', 'Searching...') : t('revenue.search', 'Search')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {t('mapsTracking.showingResultsFor', 'Showing results for')} {fromDate ? dayjs(fromDate).format('DD MMM YYYY, HH:mm:ss') : '-'}{' '}
          &rarr; {toDate ? dayjs(toDate).format('DD MMM YYYY, HH:mm:ss') : '-'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Achievement component gets raw ritase+visit data */}
          {showWidget('achievment') && (
            <Achievement
              data={achievementData}
              loading={loading || isMasterLoading}
              loadingProgress={loadingProgress}
              loadingText={loadingText || (isMasterLoading ? 'Memuat master kendaraan...' : '')}
            />
          )}

          {/* VehicleStatus component gets purely status data from custom hook */}
          {showWidget('vehicle-status') && (
            <VehicleStatus data={vehicleStatusData} loading={loading || isStatusLoading || isMasterLoading} />
          )}

          {/* Top 10 Ritase */}
          {showWidget('top-10-ritase') && (
            <TopRitaseChart categories={topRitaseData.categories} series={topRitaseData.series} isLoading={loading || isMasterLoading} />
          )}

          {/* Worst 10 Idle */}
          {showWidget('worst-10-idle-unit') && (
            <WorstIdleUnitChart
              categories={worstIdleData.categories}
              series={worstIdleData.series}
              isLoading={loading || isMasterLoading}
            />
          )}
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default PeriodeRange;
